import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { RouteUtilsService } from '@lassi/webapp-shared';
import { filter, map, switchMap } from 'rxjs';

interface IAction {
  name: unknown;
  label?: string;
}

@Component({
  selector: 'lib-action-toggle-button',
  imports: [RouterModule],
  template: `
    <a
      class="mat-label-medium"
      [class.selected]="selected()"
      [routerLink]="[action()?.name]"
      queryParamsHandling="preserve"
      [replaceUrl]="true"
    >
      {{ action()?.label ?? action()?.name }}
    </a>
  `,
  styles: `
    @use '@angular/material' as mat;
    @use 'theme' as theme;

    :host {
      flex: 1;
      border: 1px solid black;
      border-left-width: 0;

      &.selected {
        background: #e6f5fd;
      }

      &:first-of-type {
        border-top-left-radius: 50px;
        border-bottom-left-radius: 50px;
        border-left-width: 1px;
      }

      &:not(:first-of-type) {
        border-right-width: 1px;
      }

      &:last-of-type {
        border-top-right-radius: 50px;
        border-bottom-right-radius: 50px;
      }
    }

    a {
      padding-block: 0.5rem;
      text-align: center;

      width: 100%;
      display: block;
      height: 100%;
      text-decoration: none;
      color: var(--mat-button-toggle-text-color);

      &.selected {
        color: var(--mat-button-toggle-selected-state-text-color);
        font-weight: bold;
      }
    }
  `,
  host: {
    '[class.selected]': 'selected()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActionToggleButtonComponent {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly routeUtils = inject(RouteUtilsService);

  action = input<IAction>();
  selected = toSignal(
    toObservable(this.action).pipe(
      filter(Boolean),
      switchMap((action) =>
        this.routeUtils.routeEvents$.pipe(
          map(() =>
            this.router.isActive(
              this.router.createUrlTree([action.name], {
                relativeTo: this.activatedRoute,
              }),
              {
                paths: 'subset',
                matrixParams: 'ignored',
                queryParams: 'ignored',
                fragment: 'ignored',
              },
            ),
          ),
        ),
      ),
    ),
  );
}

@Component({
  selector: 'lib-action-toggle-buttons',
  imports: [ActionToggleButtonComponent],
  template: `
    @for (action of actions(); track action.name) {
      <lib-action-toggle-button [action]="action" />
    }
  `,
  styles: `
    @use '@angular/material' as mat;
    @use 'theme' as theme;

    :host {
      display: flex;
      justify-content: space-around;
      align-items: center;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActionToggleButtonsComponent {
  actions = input.required<IAction[]>();
}
